import React, { useState, useEffect } from "react";
import { DataTable, Badge, Modal, EmptyState as Empty } from "@/components/ui";
import { fmtDate } from "@/lib/formatters";
import { Eye, Check, X, Package } from "lucide-react";
import API from "@/lib/API";
import toast from "react-hot-toast";
import { FilterSection } from "@/components/ui/FilterSection";

export default function MaterialApprovalTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "" });

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      // Fetch material requests in owner mode (returns requests needing action or processed)
      const res = await API.get("/material-requests?ownerView=true");
      const list = res.data?.requests || res.data || [];
      setRequests(list);
    } catch (err) {
      console.error("Failed to fetch material requests:", err);
      toast.error("Failed to fetch material requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleActionClick = (req, type) => {
    setSelectedRequest(req);
    setActionType(type);
    setRemarks("");
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      if (actionType === "approve") {
        await API.put(`/material-requests/${selectedRequest._id}/owner-approve`);
        toast.success("Material request approved successfully");
      } else if (actionType === "reject") {
        await API.put(`/material-requests/${selectedRequest._id}/status`, {
          status: "REJECTED"
        });
        toast.success("Material request rejected successfully");
      }
      setIsModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error("Material approval action failed:", err);
      toast.error(err.response?.data?.message || `Failed to ${actionType} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRequests = React.useMemo(() => {
    let result = [...requests];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(r => 
        r.requestNo?.toLowerCase().includes(lower) ||
        r.project?.customerName?.toLowerCase().includes(lower) ||
        r.project?.projectId?.toLowerCase().includes(lower) ||
        r.installationTrade?.toLowerCase().includes(lower) ||
        r.notes?.toLowerCase().includes(lower)
      );
    }

    const { date, fromDate, toDate } = filters;
    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((r) => {
          const createdAt = new Date(r.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [requests, search, filters]);

  const columns = [
    {
      header: "Request No",
      key: "requestNo",
      render: (r) => (
        <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
          #{r.requestNo || "—"}
        </span>
      ),
    },
    {
      header: "Project ID",
      key: "projectId",
      render: (r) => (
        <span className="text-[10px] text-brand-blue font-bold uppercase tracking-wider">
          {r.project?.projectId || "—"}
        </span>
      ),
    },
    {
      header: "Customer Name",
      key: "customerName",
      render: (r) => (
        <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
          {r.project?.customerName || "—"}
        </span>
      ),
    },
    {
      header: "Department",
      key: "installationTrade",
      render: (r) => (
        <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
          {r.installationTrade || "General"}
        </span>
      ),
    },
    {
      header: "Items Count",
      key: "itemsCount",
      render: (r) => (
        <span className="text-[12px] font-semibold text-slate-700">
          {r.items?.length || 0} items
        </span>
      ),
    },
    {
      header: "Date",
      key: "createdAt",
      render: (r) => (
        <span className="text-[11px] font-medium text-slate-600">
          {fmtDate(r.createdAt)}
        </span>
      ),
    },
    {
      header: "Requested By",
      key: "requestedBy",
      render: (r) => (
        <span className="text-[11px] font-bold text-slate-600 uppercase whitespace-nowrap">
          {r.requestedBy?.name || "Staff"}
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (r) => {
        const s = r.status || "PENDING";
        const colors = {
          PENDING: "bg-amber-100 text-amber-700 border-amber-200",
          AWAITING_OWNER_APPROVAL: "bg-blue-100 text-blue-700 border-blue-200",
          APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          DISPATCHED: "bg-cyan-100 text-cyan-700 border-cyan-200",
          DELIVERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          REJECTED: "bg-red-100 text-red-700 border-red-200",
        };
        return (
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border whitespace-nowrap ${colors[s] || colors.PENDING}`}>
            {s.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      header: "Actions",
      key: "actions",
      render: (r) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleActionClick(r, "view")}
              className="p-1 text-slate-500 hover:bg-slate-50 rounded transition-colors cursor-pointer"
              title="View Details"
            >
              <Eye size={18} strokeWidth={2.5} />
            </button>
            {r.status === "AWAITING_OWNER_APPROVAL" && (
              <>
                <button
                  onClick={() => handleActionClick(r, "approve")}
                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                  title="Approve Request"
                >
                  <Check size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => handleActionClick(r, "reject")}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  title="Reject Request"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          showExport={false}
        />
      </div>

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredRequests}
          isLoading={loading}
          className="border-none shadow-none rounded-none"
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          actionType === 'view'
            ? 'Material Request Details'
            : actionType === 'approve'
            ? 'Approve Material Request'
            : 'Reject Material Request'
        }
        size="max-w-lg"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-[11px] font-bold text-slate-600 uppercase">Request No</span>
              <span className="text-[11px] font-bold text-brand-orange uppercase">#{selectedRequest?.requestNo}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] font-medium text-slate-500">Project</span>
              <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.project?.projectId} - {selectedRequest?.project?.customerName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] font-medium text-slate-500">Department</span>
              <span className="text-[13px] font-bold text-slate-800 capitalize">{selectedRequest?.installationTrade || "General"}</span>
            </div>
            {actionType === 'view' && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested By</span>
                  <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.requestedBy?.name || "Staff"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested Date</span>
                  <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.createdAt ? fmtDate(selectedRequest.createdAt) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Status</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand-blue">{selectedRequest?.status?.replace(/_/g, " ")}</span>
                </div>
                {selectedRequest?.notes && (
                  <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
                    <span className="text-[11px] font-bold text-slate-500 uppercase">Notes / Remarks</span>
                    <span className="text-[12px] text-slate-700 italic font-medium">"{selectedRequest.notes}"</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Render Items List with Total Table Details */}
          <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto overflow-x-auto no-scrollbar shadow-sm bg-white">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-[2200px]">
              <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <tr className="divide-x divide-slate-100">
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Article ID</th>
                  <th className="px-3 py-2">Vendor Name</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Sub Category</th>
                  <th className="px-3 py-2">Information</th>
                  <th className="px-3 py-2">Internal Type</th>
                  <th className="px-3 py-2">Shutter Type</th>
                  <th className="px-3 py-2">Width</th>
                  <th className="px-3 py-2">Height</th>
                  <th className="px-3 py-2">Depth</th>
                  <th className="px-3 py-2">UOM</th>
                  <th className="px-3 py-2">MOQ</th>
                  <th className="px-3 py-2">Purchased Qty</th>
                  <th className="px-3 py-2 text-indigo-600">Available Qty</th>
                  <th className="px-3 py-2">Purchase Amount</th>
                  <th className="px-3 py-2">Verve Price</th>
                  <th className="px-3 py-2">Taxable Amount</th>
                  <th className="px-3 py-2">GST %</th>
                  <th className="px-3 py-2">GST Amount</th>
                  <th className="px-3 py-2">Invoice Number</th>
                  <th className="px-3 py-2">Remarks</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Image</th>
                  <th className="px-3 py-2 font-black text-slate-700">Net Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-[11px] font-medium text-slate-700">
                {selectedRequest?.items?.map((item, idx) => {
                  const inv = item.inventoryId || {};
                  const price = inv.purchaseRate || inv.unitPrice || 0;
                  const netAmt = price * (item.quantity || 1);
                  const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);
                  const vendor = inv.vendorName || (inv.vendor?.name) || (inv.vendor?.vendorName) || "—";

                  return (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors divide-x divide-slate-50">
                      <td className="px-3 py-2 font-semibold text-slate-800">{item.itemName || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.itemCode || inv.item?.itemId || "—"}</td>
                      <td className="px-3 py-2">{vendor}</td>
                      <td className="px-3 py-2">{inv.item?.category?.categoryName || inv.item?.category?.title || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.subCategory || "—"}</td>
                      <td className="px-3 py-2 max-w-[150px] truncate" title={inv.item?.description}>{inv.item?.description || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.internalType || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.shutterType || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.width || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.height || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.depth || "—"}</td>
                      <td className="px-3 py-2 uppercase">{inv.item?.units?.title || "—"}</td>
                      <td className="px-3 py-2">{inv.item?.minQty || "—"}</td>
                      <td className="px-3 py-2">{item.quantity || "—"}</td>
                      <td className="px-3 py-2 font-bold text-indigo-600">{Number(inv.stock ?? inv.availableQty ?? 0)}</td>
                      <td className="px-3 py-2">{inv.purchaseRate ? fmt(inv.purchaseRate) : "—"}</td>
                      <td className="px-3 py-2 font-bold">{price ? fmt(price) : "—"}</td>
                      <td className="px-3 py-2">{inv.taxableValue ? fmt(inv.taxableValue) : "—"}</td>
                      <td className="px-3 py-2">{inv.taxPercent ? `${inv.taxPercent}%` : "—"}</td>
                      <td className="px-3 py-2">{inv.totalTax ? fmt(inv.totalTax) : "—"}</td>
                      <td className="px-3 py-2">{inv.invoiceNo || "—"}</td>
                      <td className="px-3 py-2 max-w-[150px] truncate" title={inv.item?.description}>{inv.item?.description || "—"}</td>
                      <td className="px-3 py-2">{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : "—"}</td>
                      <td className="px-3 py-2">{inv.item?.version || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        {inv.item?.imageUrl ? (
                          <img src={inv.item.imageUrl} alt="Item" className="w-6 h-6 object-cover rounded shadow-sm mx-auto" />
                        ) : (
                          <span className="text-slate-300 text-[10px]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-black text-slate-800">{netAmt ? fmt(netAmt) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {actionType !== 'view' && (
            <p className="text-sm text-slate-600">
              Are you sure you want to {actionType} this material request?
            </p>
          )}

          <div className="flex gap-3 pt-4">
            {actionType === 'view' ? (
              <button
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary flex-1 cursor-pointer bg-slate-800 text-white hover:bg-slate-700"
              >
                Close Details
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary flex-1 cursor-pointer"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`btn-primary flex-1 flex justify-center items-center cursor-pointer ${
                    actionType === "reject" ? "bg-red-600 hover:bg-red-700 border-red-600" : "bg-brand-orange hover:bg-orange-600 border-brand-orange"
                  }`}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    actionType === "approve" ? "Approve & Send to Store" : "Reject Request"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
